import { extractPdf, pdfToPages, t as $t, type PdfImportResult, type PdfJsDocument } from '@enercad/core';

/*
 * EPLAN schematic PDF → EnerCAD pages. pdf.js is loaded only here (it is
 * large); it reads the vector content in the browser, the conversion itself
 * lives in core.
 */

export async function importEplanPdf(file: File, progress: (msg: string) => void): Promise<PdfImportResult> {
  progress($t('PDF okuyucu yükleniyor…'));
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  // font names are needed to tell wire numbers (bold) from other texts
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()), fontExtraProperties: true });
  try {
    const doc = await task.promise;
    const data = await extractPdf(doc as unknown as PdfJsDocument, (n, of) => progress($t('Sayfa {0} / {1} okunuyor', n, of)));
    progress($t('Sayfalar çevriliyor…'));
    return pdfToPages(data);
  } finally {
    await task.destroy();
  }
}

/**
 * A drawing for the AI: as it is, or, for a provider that cannot read PDF files, the first page as a picture
 * (rendered here with pdf.js, about 2400 px on the long side).
 */
export async function aiUpload(file: File, pdfOk: boolean): Promise<{ name: string; type: string; data: string; pages?: number }> {
  if (file.type !== 'application/pdf' || pdfOk) return { name: file.name, type: file.type, data: await base64(file) };
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  try {
    const doc = await task.promise, page = await doc.getPage(1);
    const v1 = page.getViewport({ scale: 1 }), viewport = page.getViewport({ scale: 2400 / Math.max(v1.width, v1.height) });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    await page.render({ canvas, canvasContext: canvas.getContext('2d')!, viewport, background: '#fff' }).promise;
    return { name: file.name.replace(/\.pdf$/i, '') + '.png', type: 'image/png', data: canvas.toDataURL('image/png').replace(/^data:[^,]*,/, ''), pages: doc.numPages };
  } finally {
    await task.destroy();
  }
}

function base64(file: File): Promise<string> {
  return new Promise((ok, fail) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => fail(new Error($t('Dosya okunamadı.')));
    r.readAsDataURL(file);
  });
}
